import { graphqlClient, httpRequest } from "@myvp/shared/src/functions/request"
import { getErrorCode } from "@myvp/shared/src/functions/get-error-code"
import { MfaType, SupportedLocale } from "src/types"
import { graphql } from "src/gql"
import {
  type GetUserMetadataQuery,
  type ValidateDobInput,
} from "src/gql/graphql"
import { proofOfWorkHeaders } from "@myvp/shared/src/functions/proof-of-work/proof-of-work.constants"
import { HttpCode } from "@myvp/shared/src/types"
import type { ProofOfWorkSuccessResponse } from "@myvp/shared/src/functions/proof-of-work/proof-of-work.schema"
import { UnregisteredAuthResponse } from "src/models/auth.model"

export const authRequest = async (args: {
  data?: object
  url: string
  method?: string
  baseUrl?: string
  headerContentType?: "application/json" | "application/x-www-form-urlencoded"
  signal?: AbortSignal
  proofOfWork?: ProofOfWorkSuccessResponse
}) => {
  const method = args.method ?? "POST"
  const baseUrl = args.baseUrl ?? ""
  const headerContentType = args.headerContentType ?? "application/json"

  let body =
    headerContentType === "application/json"
      ? JSON.stringify({
          ...args.data,
          client: process.env.REACT_APP_auth_client,
        })
      : new URLSearchParams({
          ...args.data,
          client: process.env.REACT_APP_auth_client as string,
        }).toString()
  const headers = new Headers({
    "Content-Type": headerContentType,
  })
  if (args.proofOfWork) {
    headers.set(proofOfWorkHeaders.challenge, args.proofOfWork.challenge)
    headers.set(proofOfWorkHeaders.solution, String(args.proofOfWork.nonce))
  }
  try {
    const result = await httpRequest(`${baseUrl}/auth${args.url}`, {
      headers,
      method,
      ...(method !== "GET" ? { body } : {}),
      signal: args.signal,
    })
    const contentType = result.headers.get("content-type")
    if (
      getErrorCode(result) !== HttpCode.NoContent &&
      contentType &&
      contentType.includes("application/json")
    ) {
      const json = await result.json()
      // Specifically _httpHeaders to avoid any possible name collisions
      json._httpHeaders = result.headers
      return json
    } else {
      return { status: getErrorCode(result) }
    }
  } catch (error) {
    const e = error as {
      headers: Headers
      errorJson: object
      json: () => Promise<object>
    }
    if (e?.headers?.get?.("content-type") === "application/json") {
      e.errorJson = await e.json()
    }
    throw e
  }
}

export const resendMfaCode = async (args: {
  username?: string
  email?: string | null
  mfaType?: MfaType
  locale: string
}) => {
  if (args.mfaType === MfaType.Email) {
    return authRequest({
      url: "/send-email-verification",
      data: { loginMFA: true },
      headerContentType: "application/x-www-form-urlencoded",
    })
  } else {
    return authRequest({
      url: `/mfa/send`,
      data: {
        username: args.username,
        email: args.email ?? "",
        mfaType: args.mfaType ?? MfaType.Sms,
        locale: args.locale,
      },
      headerContentType: "application/x-www-form-urlencoded",
    })
  }
}

export const login = async (input: {
  baseUrl: string
  username: string
  password: string
  notificationRequestId: string
  ref: string
}) => {
  return authRequest({
    url: `/authorize-patient`,
    baseUrl: input.baseUrl,
    data: {
      username: input.username,
      password: input.password,
      type: process.env.REACT_APP_auth_type,
      // this indicates that we do not want to automatically send a request to Twilio
      mfaType: "none",
      ...(input.ref && input.notificationRequestId
        ? // only include the ref and id if both are present
          {
            notificationRequestId: input.notificationRequestId,
            ref: input.ref,
          }
        : {}),
    },
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const inPersonLogin = async (args: {
  authId: string
  authToken: string
}): Promise<UnregisteredAuthResponse> => {
  return authRequest({
    url: `/authorize-inperson-user`,
    data: {
      auth: args.authToken,
      authId: args.authId,
      type: process.env.REACT_APP_auth_type,
    },
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const reauth = async (input: { password: string }) => {
  return authRequest({
    url: `/reauthorize-patient`,
    data: {
      password: input.password,
    },
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const requestForgotPasswordCode = async (input: {
  username: string
  baseUrl?: string
}) => {
  return authRequest({
    url: "/forgot-password",
    baseUrl: input.baseUrl,
    data: input,
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const enterMfaCode = async (input: {
  code: string
  mfaType: MfaType
}) => {
  return authRequest({
    url: `/verify-mfa`,
    data: input,
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const verifyMfaCode = (input: { code: string; mfaType: MfaType }) => {
  return authRequest({
    data: input,
    url: "/mfa/verify",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const logout = () => {
  return authRequest({
    url: `/logout`,
    headerContentType: "application/x-www-form-urlencoded",
  })
}

const storage = sessionStorage

const metadataKey = "metadata"

export const getUserInfo = async () => {
  const query = graphql(/* GraphQL */ `
    query getUserMetadata {
      userInfo: getUserInfo {
        firstName
        lastName
        email
        formattedPhoneNumber
        phoneNumber
        pendingEmail
        timeZoneId
        username
        newTermsVersion
        authId
      }
      userStudies: getStudies {
        userStudyRoles {
          relatedUserRoleId: id
          role
          studyDetails {
            studyName
            studyId
          }
          participantDetails {
            firstName
            lastName
            relatedUserId
            siteVaultParticipantId
          }
          toDoCount
          isDefaultStudy
          languageTag
          timeZoneLabel
        }
      }
      getDoesAccountUseVirtualTime {
        virtualTimeAllowed
      }
    }
  `)
  const result = await graphqlClient.request(query)
  return result
}

export const getUserMetadata = async (): Promise<
  GetUserMetadataQuery | undefined
> => {
  if (process.env.NODE_ENV === "test") {
    return {
      userInfo: {
        formattedPhoneNumber: "1 342-444-1288",
        phoneNumber: "+13424441288",
        email: "william.walstrop@merck.com",
        timeZoneId: "America/New_York",
        authId: "auth-id",
      },
      userStudies: {
        userStudyRoles: [],
      },
      getDoesAccountUseVirtualTime: { virtualTimeAllowed: false },
    }
  }

  const item = storage.getItem(metadataKey)
  let result: GetUserMetadataQuery | undefined = undefined
  if (item) {
    try {
      result = JSON.parse(item) as GetUserMetadataQuery
    } catch {}
  }
  return result
}

export const persistUserMetadata = (metadata: GetUserMetadataQuery) => {
  storage.setItem(metadataKey, JSON.stringify(metadata))
}

export const removeUserMetadata = () => {
  storage.removeItem(metadataKey)
}

/**
 * Called to validate that a code sent to a user's email is correct. This api does
 * not require a valid JWT, and so it is different from validateNewEmailCode.
 */
export const validateEmailCode = async ({
  code,
  username,
  newEmail = "",
  mfaCode = "",
}: {
  code: string
  username?: string
  newEmail?: string
  mfaCode?: string
}) => {
  return authRequest({
    data: username ? { code, username, newEmail, mfaCode } : { code },
    url: "/validate-email-code",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

/**
 * Called when updating user's email address to mark the pending email as current.
 * Unlike validateEmailCode, this requires a verified JWT to be set and so must be called
 * after the user is logged in.
 */
export const validateNewEmailCode = async ({
  code,
  newEmail,
  mfaCode = "",
  mfaType,
}: {
  code?: string
  newEmail?: string
  mfaCode?: string
  mfaType?: MfaType
}) => {
  return authRequest({
    data: { code, newEmail, mfaCode, mfaType },
    url: "/validate-new-email-code",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

/**
 * Called to determine a user's region, given a username. If the username doesn't exist,
 * the api will still return a successful response, but the userRegionAffinity will be null.
 */
export const userDiscovery = async (
  args: {
    username: string
    baseUrl?: string
  },
  signal: AbortSignal
) => {
  return authRequest({
    url: `/user-discovery`,
    baseUrl: args.baseUrl,
    data: {
      username: args.username,
    },
    headerContentType: "application/x-www-form-urlencoded",
    signal,
  })
}

export const updatePhoneNumber = async (args: {
  updatedPhoneNumber: string
  locale: SupportedLocale
  mfaType?: MfaType
}) => {
  return authRequest({
    data: {
      updatedPhoneNumber: args.updatedPhoneNumber,
      locale: args.locale,
      mfaType: args.mfaType,
    },
    url: "/mfa/update-phone-number",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const verifyUpdatedPhoneNumber = async (args: {
  code: string
  updatedPhoneNumber: string
  mfaType: MfaType
  locale: string
}) => {
  return authRequest({
    data: {
      code: args.code,
      updatedPhoneNumber: args.updatedPhoneNumber,
      mfaType: args.mfaType,
      locale: args.locale,
    },
    url: "/mfa/verify-updated-phone-number",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const verifyEmailMfa = (token: string) => {
  return authRequest({
    url: "/verify-email",
    data: { token, loginMFA: true },
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const verifyEmail = async (token: string) => {
  return authRequest({
    url: "/verify-email",
    data: { token },
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const updateEmail = async ({
  newEmail,
  forceEmailSend = false,
  locale,
}: {
  newEmail: string
  forceEmailSend?: boolean
  locale: string
}) => {
  return authRequest({
    data: { newEmail, forceEmailSend, locale },
    url: "/userinfo/new-email",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const isValidVaultSession = async () => {
  return authRequest({
    url: "/is-valid-vault-session",
    method: "GET",
    headerContentType: "application/x-www-form-urlencoded",
  })
}

export const setUsername = async (username: string) => {
  return authRequest({
    url: "/set-username",
    data: { username },
  })
}

export const setPassword = async (password: string) => {
  return authRequest({
    url: "/set-password",
    data: { password },
  })
}

export const setEmail = async (email: string) => {
  return authRequest({
    url: "/set-email",
    data: { email },
  })
}

export const validateYob = async (input: ValidateDobInput) => {
  const mutation = graphql(/* GraphQL */ `
    mutation validateDob($input: ValidateDobInput!) {
      validateDob(input: $input) {
        success
      }
    }
  `)
  const result = await graphqlClient.request(mutation, {
    input: {
      yearOfBirth: input.yearOfBirth,
      relatedUserRoleId: input.relatedUserRoleId,
      authId: input.authId,
      activationCode: input.activationCode,
    },
  })
  return result.validateDob
}

// This is used during the forgot password flow to check MFA and the email code at once
export const authorizeMfaCodes = async (args: {
  username: string
  emailCode: string
  mfaCode: string
  mfaType?: string // BE defaults to SMS
}) => {
  return authRequest({
    url: "/authorize-mfa-codes",
    data: { ...args },
  })
}

export const validateNewActivationCode = async (activationCode: string) => {
  const mutation = graphql(/* GraphQL */ `
    mutation validateNewActivationCode($input: ValidateNewActivationInput!) {
      validateNewActivationCode(input: $input) {
        relatedUserId
        relatedUserRoleId
        authId
        dobRequired
      }
    }
  `)
  const result = await graphqlClient.request(mutation, {
    input: {
      activationCode,
    },
  })
  return result.validateNewActivationCode
}

export const confirmTerms = async (versionNumber: string) => {
  return authRequest({
    url: "/confirm-terms",
    data: { newTermsVersion: versionNumber },
  })
}
